import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Copy, Download, FileText, FileType } from "lucide-react";

export default function CompetitiveIntelligence() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requisition, setRequisition] = useState(null);

  useEffect(() => {
    fetchRequisition();
  }, [id]);

  const fetchRequisition = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}`);
      setRequisition(response.data);
    } catch (error) {
      console.error("Failed to fetch requisition:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/competitive-intelligence/export/json`);
      await navigator.clipboard.writeText(response.data.content);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/competitive-intelligence/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}_competitive_intelligence.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleExportWord = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/competitive-intelligence/export/word`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}_competitive_intelligence.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Word document downloaded!");
    } catch (error) {
      toast.error("Failed to export Word document");
    }
  };

  const handleExportText = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/competitive-intelligence/export/text`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}_competitive_intelligence.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Text file downloaded!");
    } catch (error) {
      toast.error("Failed to export text file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!requisition || !requisition.competitive_intelligence) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/requisition/${id}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Requisition
            </Button>
            <h1 className="text-xl font-bold text-slate-900">Competitive Intelligence</h1>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-6 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-600">
                No competitive intelligence data available for this requisition.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Competitive intelligence is collected when "Enhanced Research" is enabled during requisition generation.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const ci = requisition.competitive_intelligence;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/requisition/${id}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Requisition
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-900">Competitive Intelligence</h1>
                  <img 
                    src="/brain-intelligence.png" 
                    alt="Brain Intelligence" 
                    className="w-7 h-7 object-contain"
                  />
                </div>
                <p className="text-sm text-slate-600">{requisition.title}</p>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyToClipboard}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy to Clipboard</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportPDF}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export as PDF</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportWord}
                    >
                      <FileType className="w-4 h-4 mr-1" />
                      Word
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export as Word Document</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportText}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Text
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export as Text File</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Competitive Intelligence Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Competitive Intelligence</CardTitle>
              <p className="text-base text-slate-600">Insights from similar job postings in the market</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* In-Demand Skills */}
              {ci.in_demand_skills && ci.in_demand_skills.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">Top In-Demand Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {ci.in_demand_skills.map((skill, i) => (
                      <span key={i} className="text-sm px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Common Requirements */}
              {ci.common_requirements && ci.common_requirements.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">Common Requirements</h3>
                  <ul className="space-y-2">
                    {ci.common_requirements.map((req, i) => (
                      <li key={i} className="text-base text-slate-700">• {req}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Typical Qualifications */}
              {ci.typical_qualifications && (
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">Typical Qualifications</h3>
                  <p className="text-base text-slate-700">{ci.typical_qualifications}</p>
                </div>
              )}
              
              {/* Competitive Salary */}
              {ci.competitive_salary && (
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">Competitive Salary Range</h3>
                  <p className="text-lg font-medium text-slate-900">{ci.competitive_salary}</p>
                  {ci.data_quality && (
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className={`px-3 py-1 rounded-full font-semibold ${
                        ci.data_quality.confidence === 'High' ? 'bg-green-100 text-green-700' :
                        ci.data_quality.confidence === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {ci.data_quality.confidence} Confidence
                      </span>
                      {ci.data_quality.sample_count > 0 && (
                        <span className="text-slate-600">
                          {ci.data_quality.sample_count} data points
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Unique Perks */}
              {ci.unique_perks && ci.unique_perks.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">Unique Perks & Benefits</h3>
                  <ul className="space-y-2">
                    {ci.unique_perks.map((perk, i) => (
                      <li key={i} className="text-base text-slate-700">• {perk}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Data Sources */}
              {ci.data_quality && ci.data_quality.sources && ci.data_quality.sources.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">Data Sources</h3>
                  <div className="flex flex-wrap gap-2">
                    {ci.data_quality.sources.map((source, i) => (
                      <span key={i} className="text-sm px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
